// src/utils/translation.js

/**
 * Translation utility for Vue.js app integrated with WordPress/WPML
 */

// This will hold our loaded translations
let translations = {
  'en': {},
  'it': {}
};

// Flag to track if translations have been loaded
let translationsLoaded = false;

// Default language fallback
const DEFAULT_LANGUAGE = 'en';

/**
 * Ottieni l'URL delle traduzioni dal WordPress
 * @returns {string} URL base per i file di traduzione
 */
function getTranslationsPath () {

  // Prima opzione: usa il percorso specifico per le traduzioni se disponibile
  if (typeof window.rolexCalendarSettings !== 'undefined' &&
      window.rolexCalendarSettings.translationsPath) {
    return window.rolexCalendarSettings.translationsPath;
  }

  // Seconda opzione: usa l'URL base del plugin + percorso traduzioni
  if (typeof window.rolexCalendarSettings !== 'undefined' &&
      window.rolexCalendarSettings.pluginUrl) {
    return window.rolexCalendarSettings.pluginUrl + 'translations/';
  }

  // Fallback: utilizza un percorso relativo (potrebbe non funzionare)
  console.warn('Could not determine translations path, using relative path.');
  return './translations/';
}

/**
 * Carica i file di traduzione in base alla lingua rilevata
 * @returns {Promise} Promise che si risolve quando le traduzioni sono caricate
 */
async function loadTranslations() {
  try {
    const currentLang = getCurrentLanguageCode();
    const translationsPath = getTranslationsPath();

    // Carica sempre l'inglese come fallback
    const enUrl = `${translationsPath}en.json`;

    try {
      const enResponse = await fetch(enUrl);

      if (!enResponse.ok) {
        throw new Error(`Failed to fetch English translations: ${enResponse.status} ${enResponse.statusText}`);
      }

      const enData = await enResponse.json();
      translations['en'] = enData;
    } catch (error) {
      console.error('Error loading English translations:', error);
    }

    // Carica la lingua corrente se non è l'inglese
    if (currentLang !== 'en') {
      try {
        const langUrl = `${translationsPath}${currentLang}.json`;

        const langResponse = await fetch(langUrl);

        if (!langResponse.ok) {
          throw new Error(`Failed to fetch ${currentLang} translations: ${langResponse.status} ${langResponse.statusText}`);
        }

        const langData = await langResponse.json();
        translations[currentLang] = langData;
      } catch (error) {
        console.warn(`Could not load translations for ${currentLang}, using English as fallback.`, error);
      }
    }

    translationsLoaded = true;
    return translations;
  } catch (error) {
    console.error('Error in loadTranslations:', error);
    translationsLoaded = false;
    return {};
  }
}

/**
 * Ottiene il codice lingua corrente da WordPress/WPML
 * @returns {string} Codice lingua (es. 'en', 'it')
 */
function getCurrentLanguageCode() {
  // Prima opzione: usa la lingua WPML fornita da WordPress
  if (typeof window.rolexCalendarSettings !== 'undefined' &&
      window.rolexCalendarSettings.wpmlLanguage) {
    return window.rolexCalendarSettings.wpmlLanguage;
  }

  // Seconda opzione: usa la lingua del sito fornita da WordPress
  if (typeof window.rolexCalendarSettings !== 'undefined' &&
      window.rolexCalendarSettings.siteLanguage) {
    return window.rolexCalendarSettings.siteLanguage;
  }

  // Terza opzione: usa la variabile globale di WPML
  if (typeof window.wpml_browser_redirect_params !== 'undefined' &&
      window.wpml_browser_redirect_params.lang) {
    return window.wpml_browser_redirect_params.lang;
  }

  // Quarta opzione: cerca attributi data-lang
  const dataLang = document.body.getAttribute('data-lang') ||
                  document.documentElement.getAttribute('data-lang');
  if (dataLang) {
    return dataLang.split('-')[0];
  }

  // Ultima opzione: usa l'attributo lang dell'HTML
  const htmlElement = document.documentElement;
  const langAttribute = htmlElement.getAttribute('lang') || 'en';

  // Estrai il codice lingua principale (es. 'it-IT' -> 'it')
  const mainLang = langAttribute.split('-')[0];

  // Lista delle lingue supportate
  const supportedLanguages = ['en', 'it'];

  // Ritorna il codice lingua o fallback a DEFAULT_LANGUAGE se non supportato
  return supportedLanguages.includes(mainLang) ? mainLang : DEFAULT_LANGUAGE;
}

/**
 * Ottiene un valore annidato da un oggetto usando un percorso in notazione puntata
 * @param {Object} obj - L'oggetto in cui cercare
 * @param {string} path - Percorso in notazione puntata (es. 'appointment.book_appointment')
 * @returns {string|undefined} Il valore o undefined se non trovato
 */
function getNestedValue (obj, path) {
  return path.split('.').reduce((prev, curr) => {
    return prev ? prev[curr] : undefined;
  }, obj);
}

/**
 * Traduce una chiave in base alla lingua corrente
 * @param {string} key - Chiave di traduzione (può essere in notazione puntata per oggetti annidati)
 * @param {Object} replacements - Coppie chiave-valore opzionali per contenuti dinamici
 * @returns {string} Stringa tradotta
 */
function translate(key, replacements = {}) {
  // Verifica che le traduzioni siano caricate
  if (!translationsLoaded) {
    console.warn('Translations not loaded yet. Using key as fallback.');
    return key;
  }

  const lang = getCurrentLanguageCode();

  // Ottieni il testo tradotto dalla struttura annidata
  let text = getNestedValue(translations[lang], key);

  // Fallback all'inglese se la chiave non esiste nella lingua corrente
  if (text === undefined) {
    text = getNestedValue(translations[DEFAULT_LANGUAGE], key);
  }

  // Se ancora undefined, usa la chiave stessa
  if (text === undefined) {
    return key;
  }

  // Sostituisci eventuali contenuti dinamici
  Object.keys(replacements).forEach(replaceKey => {
    text = text.replace(new RegExp(`{${replaceKey}}`, 'g'), replacements[replaceKey]);
  });

  return text;
}

/**
 * Plugin Vue per aggiungere funzionalità di traduzione
 */
const TranslationPlugin = {
  install(Vue) {
    // Carica le traduzioni prima
    loadTranslations().then(() => {
    }).catch(error => {
      console.error('Failed to load translations:', error);
    });

    // Aggiungi il metodo $t alle istanze Vue
    Vue.prototype.$t = translate;

    // Aggiungi un filtro globale
    Vue.filter('translate', translate);
  }
};

export { translate, getCurrentLanguageCode, loadTranslations, TranslationPlugin };